/**
 * [JWT 토큰 관리] - 액세스 토큰 저장, 갱신, 만료 처리
 *
 * - 액세스 토큰은 메모리에만 저장 (XSS 방어, localStorage 미사용)
 * - 리프레시 토큰은 HttpOnly 쿠키로 관리 (Next.js 프록시 경유)
 * - 만료 1분 전 선제적 토큰 갱신 타이머 설정
 * - 동시 갱신 요청 중복 방지 (단일 in-flight Promise 공유)
 */
import toast from 'react-hot-toast';
import { wsManager } from './websocket';
import { isCredentialPreservingContext } from './liveMediaSession';
import { classifyRefreshFailure, type RefreshOutcome } from './refreshFailure';

// 액세스 토큰 저장소 (메모리 전용, localStorage 사용 안 함)
let accessToken: string | null = null;

// 선제적 토큰 갱신 + transient 재시도 타이머 핸들(단일 핸들 공유 → clearTokens/로그아웃이 자동 취소).
let refreshTimer: ReturnType<typeof setTimeout> | null = null;

// 중복 방지: 갱신 요청은 동시에 하나만 진행 (타이머, 401 핸들러, 초기화, WebSocket 등이 공유)
let refreshInFlight: Promise<RefreshOutcome> | null = null;

/**
 * JWT 토큰에서 만료 시간(exp)을 추출한다. (외부 라이브러리 미사용)
 * base64url 인코딩된 페이로드를 디코딩하여 exp 클레임을 밀리초로 반환한다.
 */
function parseJwtExpiry(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    // base64url → 표준 base64 변환: URL 안전 문자 치환 + 패딩 추가
    let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4;
    if (pad) {
      base64 += '='.repeat(4 - pad);
    }

    const payload = JSON.parse(atob(base64));
    return payload.exp ? payload.exp * 1000 : null; // 초 → 밀리초 변환
  } catch {
    // JWT 형식이 아니거나 base64 디코딩 실패 — 만료된 것으로 간주하여 null 반환
    return null;
  }
}

/** 메모리에 저장된 현재 액세스 토큰을 반환한다. */
export function getAccessToken(): string | null {
  return accessToken;
}

/**
 * 임베드 Ghost 제어 토큰 (메모리 전용, 액세스 토큰과 같은 보관 방식).
 *
 * 임베드 iframe에는 로그인 세션이 없어 액세스 토큰이 없다. Ghost는 접속 직후
 * `/api/public/meetings/{id}/ghost/claim-control`로 교환한 이 토큰을 대신 쓰고,
 * `apiFetch`가 `X-Ghost-Control-Token` 헤더로 실어 보낸다. 서버 필터가 이를
 * Ghost 사용자 인증으로 승격하므로 기존 제어 엔드포인트가 그대로 동작한다.
 */
let ghostControlToken: string | null = null;
let onGhostControlInvalidated: (() => void) | null = null;
const ghostControlTokenListeners = new Set<() => void>();

export function getGhostControlToken(): string | null {
  return ghostControlToken;
}

export function setGhostControlToken(token: string | null, onInvalidated?: () => void): void {
  const changed = ghostControlToken !== token;
  const invalidate = token === null ? onGhostControlInvalidated : null;
  ghostControlToken = token;
  onGhostControlInvalidated = token ? (onInvalidated ?? null) : null;
  invalidate?.();
  if (changed) ghostControlTokenListeners.forEach((listener) => listener());
}

export function subscribeGhostControlToken(listener: () => void): () => void {
  ghostControlTokenListeners.add(listener);
  return () => {
    ghostControlTokenListeners.delete(listener);
  };
}

/** 액세스 토큰을 메모리에 저장하고 선제적 갱신 타이머를 설정한다. */
export function setAccessToken(token: string): void {
  accessToken = token;
  setupTokenRefreshTimer(token);
  // WebSocket 재연결 시 사용할 토큰도 동기화
  wsManager.updateToken(token);
}

/** 액세스 토큰을 메모리에서 삭제하고 갱신 타이머를 취소한다. */
export function clearTokens(): void {
  accessToken = null;
  clearRefreshTimer();
}

/**
 * Next.js API 라우트를 통해 HttpOnly 리프레시 토큰 쿠키를 삭제한다.
 * 리다이렉트 전에 await해야 미들웨어가 쿠키 삭제를 감지할 수 있다.
 */
export async function clearRefreshCookie(): Promise<void> {
  try {
    await fetch('/api/clear-cookie', { method: 'POST' });
  } catch {
    // 최선 노력: 실패해도 미들웨어가 리다이렉트 처리함
  }
}

/**
 * 리프레시 토큰 쿠키로 액세스 토큰 갱신을 시도하고 상세 결과(성공/transient/definitive)를 반환한다.
 *
 * 중복 방지: 이미 진행 중인 갱신이 있으면 동일 Promise를 공유한다(동시 401 → 단일 리프레시).
 */
export async function refreshAccessTokenDetailed(): Promise<RefreshOutcome> {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = doRefresh().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

/**
 * 액세스 토큰 갱신을 시도한다. 성공 시 true, 실패 시 false 반환.
 *
 * <p>공개 시그니처 보존용 얇은 래퍼다(AuthContext·WebSocketContext·MeetingChat·WS tokenProvider 소비).
 * 상세 실패 종류가 필요하면 {@link refreshAccessTokenDetailed} 를 쓴다.</p>
 */
export async function refreshAccessToken(): Promise<boolean> {
  return (await refreshAccessTokenDetailed()).ok;
}

async function doRefresh(): Promise<RefreshOutcome> {
  try {
    // 크로스 오리진 쿠키 문제를 피하기 위해 Next.js 프록시 사용
    // (localhost:3001 → localhost:8081 간 쿠키가 자동 누락될 수 있음)
    // 프록시(route.ts)는 백엔드 오류를 원상태로 포워딩하고, 백엔드 연결 실패는 502 로 준다.
    const response = await fetch('/api/auth/refresh', {
      method: 'POST',
    });

    if (!response.ok) {
      // 4xx=definitive(세션 무효), 5xx/429/408=transient(재시도).
      return { ok: false, kind: classifyRefreshFailure(response.status) };
    }

    const data = await response.json();
    if (data.accessToken) {
      setAccessToken(data.accessToken);
      return { ok: true };
    }

    // 200 인데 accessToken 없음 — 재시도해도 무의미(확정 실패).
    return { ok: false, kind: 'definitive' };
  } catch {
    // fetch 자체가 reject(네트워크 단절/타임아웃) — 재시도 가치 있는 일시적 실패.
    return { ok: false, kind: classifyRefreshFailure(null) };
  }
}

/**
 * 액세스 토큰 만료 1분 전에 선제적으로 갱신하는 타이머를 설정한다.
 * 이미 만료되었거나 60초 이내에 만료되면 즉시 갱신을 시도한다.
 * 갱신 실패 시 토큰을 삭제하고 로그인 페이지로 리다이렉트한다.
 */
export function setupTokenRefreshTimer(token: string): void {
  // 기존 타이머가 있으면 먼저 제거
  clearRefreshTimer();

  const expiry = parseJwtExpiry(token);
  if (!expiry) return;

  const now = Date.now();
  const refreshAt = expiry - 60_000; // 만료 1분 전에 갱신
  const MAX_TIMEOUT = 2_147_483_647; // setTimeout 최댓값: 2^31 - 1 ms (~24.8일)
  const delay = Math.min(Math.max(refreshAt - now, 0), MAX_TIMEOUT);

  refreshTimer = setTimeout(() => {
    void performScheduledRefresh(1);
  }, delay);
}

/** 대기 중인 토큰 갱신/재시도 타이머를 취소한다. */
export function clearRefreshTimer(): void {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

/**
 * 예약된(선제/재시도) 리프레시를 실행하고 결과에 따라 분기한다.
 *  - 성공: 안내 토스트 닫음(setAccessToken 이 이미 선제 타이머 재설정).
 *  - definitive: 기존과 동일하게 로그아웃(clear + 쿠키삭제 + redirectToLogin — 라이브 격하는 redirectToLogin 내부).
 *  - transient: 로그아웃하지 않고 백오프 재시도(scheduleRefreshRetry).
 *
 * @param nextRetryAttempt transient 로 재시도할 때 사용할 다음 시도 번호(1부터).
 */
async function performScheduledRefresh(nextRetryAttempt: number): Promise<void> {
  const outcome = await refreshAccessTokenDetailed();
  if (outcome.ok) {
    dismissReauthRetry();
    return;
  }
  if (outcome.kind === 'definitive') {
    clearTokens();
    await clearRefreshCookie();
    redirectToLogin();
    return;
  }
  // transient — 세션은 유효, 일시적 실패. 로그아웃 금지 + 백오프 재시도.
  scheduleRefreshRetry(nextRetryAttempt);
}

/**
 * transient 리프레시 실패에 대해 로그아웃 없이 무한 백오프 재시도를 예약한다.
 *
 * <p>지연 = min(5000·2^(attempt-1), 60000). **무지터**(단일 백엔드 대상 — thundering-herd 없음;
 * 지터는 P2-C 재연결에만 적용하는 의도된 비대칭). refreshTimer 핸들을 재사용하므로
 * clearTokens()/로그아웃이 자동 취소한다. 실제 만료(401=definitive)면 다음 시도가 자기종료한다.</p>
 */
export function scheduleRefreshRetry(attempt: number = 1): void {
  clearRefreshTimer();
  // 라이브(자격증명 보존) 컨텍스트에서 최초 transient 시 비차단 안내 1회.
  if (
    attempt === 1 &&
    typeof window !== 'undefined' &&
    isCredentialPreservingContext(window.location.pathname)
  ) {
    notifyReauthRetry();
  }
  const delay = Math.min(5000 * 2 ** (attempt - 1), 60_000);
  refreshTimer = setTimeout(() => {
    void performScheduledRefresh(attempt + 1);
  }, delay);
}

/**
 * JWT 토큰이 만료되었거나 60초 이내에 만료되는지 확인한다.
 * 파싱 실패 시 만료된 것으로 간주한다.
 */
export function isTokenExpired(token: string): boolean {
  const expiry = parseJwtExpiry(token);
  if (!expiry) return true;
  return Date.now() >= expiry - 60_000;
}

/**
 * 라이브 회의 중 재인증 안내에 사용하는 고정 토스트 ID.
 * 반복 401(예: 대기실 5초 폴링)에도 토스터가 동일 id로 dedup하여 세션당 하나만 노출한다.
 */
export const REAUTH_TOAST_ID = 'reauth-required';

/**
 * 라이브 회의 중 세션이 만료됐을 때, 하드 리다이렉트로 회의를 끊는 대신
 * 비차단 안내만 노출한다. 고정 id로 반복 호출이 스팸되지 않는다.
 */
export function notifyReauthRequired(): void {
  toast.error('세션이 만료되었습니다. 다시 로그인하면 회의 제어가 복구됩니다.', {
    id: REAUTH_TOAST_ID,
  });
}

/**
 * transient 리프레시 재시도 중임을 알리는 고정 토스트 id. 라이브 컨텍스트에서 최초 1회 노출하고,
 * 성공 시 dismiss 한다(definitive 로그아웃과 구분되는 별도 id).
 */
export const REAUTH_RETRY_TOAST_ID = 'reauth-retry';

/** 라이브 중 일시적 재인증 재시도 안내(비차단). */
function notifyReauthRetry(): void {
  toast.loading('연결이 불안정합니다. 자동으로 다시 연결하고 있습니다…', {
    id: REAUTH_RETRY_TOAST_ID,
  });
}

/** 재시도 안내 토스트를 닫는다(리프레시 성공 시). */
function dismissReauthRetry(): void {
  toast.dismiss(REAUTH_RETRY_TOAST_ID);
}

/**
 * 로그인 페이지로 리다이렉트한다.
 * 현재 경로를 returnUrl 파라미터로 포함하여 로그인 후 복귀할 수 있게 한다.
 *
 * 단, **라이브 회의(in-room) 경로에서는 하드 네비게이션을 억제(격하)한다**:
 * `window.location.href` 이동은 회의 페이지를 언로드시켜 참가자를 회의에서
 * 튕겨내기 때문이다("갑자기 튕김"의 유일 트리거). 이 경우 비차단 재인증 안내만
 * 노출하고 회의 세션은 그대로 유지한다. (`clearTokens`는 별개 경로에서 호출되며
 * AuthContext.isAuthenticated 는 건드리지 않으므로 페이지 언마운트도 없다.)
 * 비회의 경로에서는 기존 하드 리다이렉트를 보존한다.
 */
export function redirectToLogin(): void {
  if (typeof window === 'undefined') return;

  const currentPath = window.location.pathname + window.location.search;
  const loginPath = '/login';

  // 이미 로그인 페이지에 있으면 무한 리다이렉트 방지
  if (currentPath.startsWith(loginPath)) return;

  // 자격증명 보존 컨텍스트(라이브 회의 경로 || 라이브 미디어 세션 생존):
  // 하드 네비 억제 + 비차단 안내로 격하(RC-1 근본 수정 + embed/prejoin 세션 보호).
  if (isCredentialPreservingContext(window.location.pathname)) {
    notifyReauthRequired();
    return;
  }

  const returnUrl = encodeURIComponent(currentPath);
  window.location.href = `${loginPath}?returnUrl=${returnUrl}`;
}
